import { supabase } from '@/lib/supabase';
import { logUpdate } from '@/lib/audit-log';

/* BMC-internal hold on a video (cr_video_submissions.admin_flagged_*). While
 * admin_flagged_at is set the worker's crons never auto-approve the video and
 * never auto-refund its hire. Unrelated to the retired brand_flag_* columns. */

export type AdminFlagFields = {
  admin_flagged_at: string | null;
  admin_flag_reason: string | null;
  admin_flagged_by_email: string | null;
};

export const ADMIN_FLAG_COLUMNS = 'admin_flagged_at, admin_flag_reason, admin_flagged_by_email';
export const ADMIN_FLAG_REASON_MIN = 3;
export const ADMIN_FLAG_REASON_MAX = 2000;

/** How many of a hire's (non-deleted) videos a BMC admin has flagged. Used by
 *  the Creator Payments "Flagged" filter and its per-hire badge, alongside the
 *  brand's own flag count (lib/video-flags.ts flagCounts) — the two are
 *  separate features and neither implies the other. */
export function adminFlagCount(videos: Array<Pick<AdminFlagFields, 'admin_flagged_at'>> | null | undefined): number {
  return (videos ?? []).filter(v => !!v.admin_flagged_at).length;
}

/** Only a video still awaiting review, on a hire that isn't paid out or settled.
 *  Mirrors cr_video_submissions_admin_flag_guard (20260929000002/000003).*/
export function canAdminFlag(
  video: { review_status: string | null; deleted: boolean | null } & Partial<AdminFlagFields>,
  hire: { payment_status: string | null; settled_at: string | null } | null | undefined,
): boolean {
  return !!hire
    && !video.admin_flagged_at
    && video.review_status == null
    && !video.deleted
    && hire.payment_status !== 'paid'
    && !hire.settled_at;
}

type Ctx = { hireId: string; tiktokVideoUrl: string | null };

export async function flagVideo(videoId: string, reason: string, ctx: Ctx): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const payload: AdminFlagFields = {
    admin_flagged_at: new Date().toISOString(),
    admin_flag_reason: reason.trim(),
    admin_flagged_by_email: session?.user?.email ?? null,
  };
  const meta = { hire_id: ctx.hireId, tiktok_video_url: ctx.tiktokVideoUrl };
  try {
    const { data, error } = await supabase
      .from('cr_video_submissions')
      .update(payload)
      .eq('id', videoId)
      .is('admin_flagged_at', null)
      .is('review_status', null)
      .eq('deleted', false)
      .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error('This video was reviewed or flagged since the page loaded — refresh and try again.');
    await logUpdate('cr_video_submissions', videoId, { admin_flagged_at: null, admin_flag_reason: null, admin_flagged_by_email: null }, payload, {
      ...meta,
      status: 'success',
      description: `Admin flagged a video submission (holds auto-approve / auto-refund) — reason: ${payload.admin_flag_reason}`,
    });
  } catch (e: any) {
    const message = e?.message ?? 'Failed to flag video';
    await logUpdate('cr_video_submissions', videoId, { admin_flagged_at: null }, payload, {
      ...meta,
      status: 'failed',
      error: message,
      description: 'Failed to flag a video submission',
    });
    throw new Error(message);
  }
}
